import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBasket } from "@/context/BasketContext";
import { Button } from "@/components/ui/button";
import { PenLine, ShoppingBasket, CalendarCheck } from "lucide-react";
import ProfileMenu from "@/components/ProfileMenu";
import NotificationsBell from "@/components/NotificationsBell";

export default function Navbar() {
  const { user, login } = useAuth();
  const { count } = useBasket();
  const navigate = useNavigate();

  const linkCls = ({ isActive }) =>
    `accent-label transition-colors ${isActive ? "text-[#4E5A3D]" : "text-[#2A2823]/60 hover:text-[#2A2823]"}`;

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FBF8F1]/80 border-b border-[#B79256]/25">
      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2 sm:gap-6">
        <Link to="/" data-testid="brand-link" className="flex items-center shrink-0">
          <img
            src="/willow-wordmark.png"
            alt="Willoah"
            className="h-10 sm:h-14 md:h-16 w-auto -my-2 select-none"
            draggable={false}
          />
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/grove" data-testid="nav-grove" className={linkCls}>The Grove</NavLink>
          <NavLink to="/try-and-critique" data-testid="nav-try-critique" className={linkCls}>Try &amp; Critique</NavLink>
          <NavLink to="/how-it-works" data-testid="nav-how-it-works" className={linkCls}>How it works</NavLink>
          {user && <NavLink to="/planner/home" data-testid="nav-planner" className={linkCls}>Planner</NavLink>}
          {user && <NavLink to={`/roots/${user.user_id}`} data-testid="nav-roots" className={linkCls}>Roots</NavLink>}
          {user?.is_owner && <NavLink to="/moderation" data-testid="nav-moderation" className={linkCls}>Moderation</NavLink>}
        </nav>
        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
          {user && (
            // Mobile-only Planner shortcut — the desktop nav row shows a
            // labeled `Planner` link, but on phone we replace that with a
            // compact icon here so signed-in users have a single-tap route
            // to /planner/home without opening the profile menu.
            <Link
              to="/planner/home"
              data-testid="nav-planner-mobile"
              aria-label="Willoah Planner"
              title="Willoah Planner"
              className="md:hidden p-2 rounded-full hover:bg-[#B79256]/12 text-[#4E5A3D] transition-colors"
            >
              <CalendarCheck className="w-5 h-5" strokeWidth={1.6} />
            </Link>
          )}
          <Link
            to="/basket"
            data-testid="basket-link"
            aria-label={`Meal plan basket (${count})`}
            title="Meal plan basket"
            className="relative p-2 rounded-full hover:bg-[#B79256]/12 text-[#2A2823]/70 hover:text-[#2A2823] transition-colors"
          >
            <ShoppingBasket className="w-5 h-5" strokeWidth={1.6} />
            {count > 0 && (
              <span
                data-testid="basket-count"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#4E5A3D] text-[#FBF8F1] text-[10px] font-semibold flex items-center justify-center border border-[#FBF8F1]"
              >
                {count}
              </span>
            )}
          </Link>
          {user ? (
            <>
              <NotificationsBell />
              <Button
                data-testid="plant-idea-nav-btn"
                onClick={() => navigate("/plant")}
                className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] px-3 sm:px-5 h-9 sm:h-10 text-sm shadow-none"
              >
                <PenLine className="w-4 h-4 sm:mr-2" strokeWidth={1.75} />
                <span className="hidden sm:inline">Plant a seed</span>
              </Button>
              <ProfileMenu />
            </>
          ) : (
            <Button
              asChild
              data-testid="login-btn"
              className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] px-3 sm:px-5 h-9 sm:h-10 text-xs sm:text-sm shadow-none whitespace-nowrap"
            >
              <Link to="/login">Sign in</Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
